import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DOWNLOAD_ARCHIVED_LIST, GET_ARCHIVE_LIST } from "../../../enums/ApiPaths";
import { ARCHIVE_LIST } from "../../../enums/QueryKeys";
import { SINGLE_ARCHIVE_VIEW_NO_ID } from "../../../enums/UrlPaths";
import { useFetch } from "../../../hooks/Fetching";
import api from "../../../utils/api/authApi";
import { ErrorMessage } from "../components/display/NotFound";
import HorizontalStack from "../components/wrappers/HorizontalStack";
const TABLE_HEIGHT = 600;

export default function Archive() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const path = GET_ARCHIVE_LIST;
  const url = null;

  const { data = [], isLoading, error } = useFetch(path, null, ARCHIVE_LIST);

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  if (isLoading) return <LinearProgress sx={{ mt: 10 }} />;
  if (error)
    return (
      <ErrorMessage
        title={t("SomethingWentWrong")}
        subtitle={t("FailedToFetchArchivedData")}
      />
    );

  const handleExportCsv = async () => {
    const response = await api.post(
      DOWNLOAD_ARCHIVED_LIST,
      {},
      {
        responseType: "blob",
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `archive_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "index", label: "#", width: "15%" },
    { key: "petName", label: "PetName", width: "20%" },
    { key: "ownerEmail", label: "OwnerName", width: "20%" },
    { key: "lastArchiveTitle", label: "Therapy", width: "20%" },
    { key: "dateOfArchive", label: "DateOfArchive", width: "20%" },
  ];

  const getValue = (stay, key) => {
    return stay?.[key] ?? "N/A";
  };

  const visibleRows = data?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ mt: 10, p: 3 }}>
      <HorizontalStack>
        <Typography variant="h4" fontWeight={600} mb={3}>
          {t("Archive")}
        </Typography>
        <IconButton
          onClick={() => handleExportCsv()}
          title={t("DownloadArchiveDataInCsvFormat")}
        >
          <DownloadIcon />
        </IconButton>
      </HorizontalStack>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: TABLE_HEIGHT, overflowY: "auto" }}>
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow sx={{ height: 50 }}>
                {columns.map(({ key, label, width }) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontWeight: 600,
                      width,
                    }}
                  >
                    <TableSortLabel disabled={true}>{t(label)}</TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((stay, index) => (
                <TableRow
                  onClick={() =>
                    navigate(`${SINGLE_ARCHIVE_VIEW_NO_ID}${stay.inpatientId}`)
                  }
                  key={stay.id}
                  hover
                  sx={{
                    height: 50,
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                    "&:hover": { backgroundColor: "grey.100" },
                  }}
                >
                  <TableCell sx={{ width: columns[0].width }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  {columns.slice(1).map(({ key, width }) => (
                    <TableCell
                      key={key}
                      sx={{
                        width,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      align="left"
                    >
                      {getValue(stay, key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t("RowsPerPage")}
          backIconButtonProps={{
            title: t("PreviousPage"),
            "aria-label": t("PreviousPage"),
          }}
          nextIconButtonProps={{
            title: t("NextPage"),
            "aria-label": t("NextPage"),
          }}
        />
      </Paper>
    </Box>
  );
}
